import type { HomePartDefinition, HomeRoomData } from "@/home/storage";
import { resolveRoomBackdropUrl } from "@/home/storage";
import { themeById, type HomeThemeId } from "@/home/themes";

/** Part ids + theme — safe for publish / cloud (images resolved from sceneParts at battle time). */
export type TowerArenaAmbienceConfig = {
  themeId: HomeThemeId;
  backdropUrl: string | null;
  shellPartId?: string;
  backdropPartId?: string;
  floorPartId?: string;
};

/** Resolved image URLs for Phaser (dungeon trial / raid battle). */
export type TowerArenaAmbienceResolved = {
  themeId: HomeThemeId;
  backdropCustomUrl: string | null;
  shellImageDataUrl?: string;
  backdropImageDataUrl?: string;
  floorSkinImageDataUrl?: string;
};

function partImageDataUrl(
  parts: HomePartDefinition[],
  partId: string | undefined,
): string | undefined {
  if (!partId) return undefined;
  const part = parts.find((p) => p.id === partId);
  const url = part?.imageDataUrl ?? "";
  return url.startsWith("data:") ? url : undefined;
}

export function buildTowerArenaAmbienceConfig(room: HomeRoomData): TowerArenaAmbienceConfig {
  return {
    themeId: themeById(room.themeId).id,
    backdropUrl: resolveRoomBackdropUrl(room),
    shellPartId: room.shellPartId ?? undefined,
    backdropPartId: room.backdropPartId ?? undefined,
    floorPartId: room.floorPartId ?? undefined,
  };
}

export function resolveTowerArenaAmbience(
  config: TowerArenaAmbienceConfig | undefined,
  sceneParts: HomePartDefinition[],
): TowerArenaAmbienceResolved | undefined {
  if (!config) return undefined;
  return {
    themeId: config.themeId,
    backdropCustomUrl: config.backdropUrl,
    shellImageDataUrl: partImageDataUrl(sceneParts, config.shellPartId),
    backdropImageDataUrl: partImageDataUrl(sceneParts, config.backdropPartId),
    floorSkinImageDataUrl: partImageDataUrl(sceneParts, config.floorPartId),
  };
}

export function collectAmbiencePartIds(room: HomeRoomData): string[] {
  const ids: string[] = [];
  if (room.shellPartId) ids.push(room.shellPartId);
  if (room.backdropPartId) ids.push(room.backdropPartId);
  if (room.floorPartId) ids.push(room.floorPartId);
  return ids;
}
